import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import { compareRosterValue, compareStudentsForRoster } from '../utils/gradeClass'

export type User = Database['public']['Tables']['users']['Row']
export type UserInsert = Database['public']['Tables']['users']['Insert']
export type UserUpdate = Database['public']['Tables']['users']['Update']

interface RosterOrderable<T> {
  order: (column: string, options?: { ascending?: boolean; nullsFirst?: boolean }) => T
}

/**
 * 名簿順（学年 → クラス → 出席番号 → 名前）で並べる。
 * *_sort は値の数字だけを取り出した生成列（database/111_add_user_roster_sort_columns.sql）。
 * 数字を含まないクラス（'A組' や '建築学分野'）は NULL になるので nullsFirst: false で後ろに回し、
 * 続く文字列カラムで並べる。最後の id はページ境界を一意にするため。
 */
export const applyRosterOrder = <T extends RosterOrderable<T>>(query: T): T =>
  query
    .order('grade_sort', { ascending: true, nullsFirst: false })
    .order('grade', { ascending: true, nullsFirst: false })
    .order('class_sort', { ascending: true, nullsFirst: false })
    .order('class', { ascending: true, nullsFirst: false })
    .order('attendance_sort', { ascending: true, nullsFirst: false })
    .order('attendance_number', { ascending: true, nullsFirst: false })
    .order('name', { ascending: true })
    .order('id', { ascending: true })

// グループで絞るための埋め込み。group_members には users への FK が2つ（student_id / added_by）あるので FK 名で指定する。
// 生徒IDを .in() で渡すと大きなグループで GET の URL 長が破綻するので、!inner の埋め込みで絞る。
const GROUP_MEMBER_EMBED = 'group_members!group_members_student_id_fkey!inner(group_id)'

export class UserService {
  // Get all users (admin only)
  static async getUsers(filters?: {
    role?: string
    schoolId?: string
    search?: string
    grade?: string
    class?: string
    // 'prefix' は管理者の自由入力欄用の前方一致。既定は学校の選択肢から選んだ値の完全一致
    classMatch?: 'exact' | 'prefix'
    groupId?: string
    limit?: number
    offset?: number
    // 'roster' は名簿順（学年 → クラス → 出席番号）。既定は登録が新しい順
    orderBy?: 'created_at' | 'roster'
  }): Promise<User[]> {
    let query = supabase
      .from('users')
      .select(`
        *,
        schools(name)${filters?.groupId ? `,\n        ${GROUP_MEMBER_EMBED}` : ''}
      `)

    if (filters?.orderBy === 'roster') {
      query = applyRosterOrder(query)
    } else {
      query = query.order('created_at', { ascending: false })
    }

    if (filters?.role) {
      query = query.eq('role', filters.role)
    }

    if (filters?.schoolId) {
      query = query.eq('school_id', filters.schoolId)
    }

    if (filters?.grade) {
      query = query.eq('grade', filters.grade)
    }

    if (filters?.class) {
      query = filters.classMatch === 'prefix'
        ? query.ilike('class', `${filters.class}%`)
        : query.eq('class', filters.class)
    }

    if (filters?.groupId) {
      query = query.eq('group_members.group_id', filters.groupId)
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
    }

    if (filters?.limit) {
      query = query.limit(filters.limit)
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
    }

    const { data, error } = await query

    if (error) throw error
    // グループの埋め込みを足すと select が動的な文字列になり型推論が効かないため明示する
    return (data || []) as unknown as User[]
  }

  static async getUsersCount(filters?: {
    role?: string
    schoolId?: string
    search?: string
    grade?: string
    class?: string
    classMatch?: 'exact' | 'prefix'
    groupId?: string
  }): Promise<number> {
    let query = supabase
      .from('users')
      .select(filters?.groupId ? `id, ${GROUP_MEMBER_EMBED}` : '*', { count: 'exact', head: true })

    if (filters?.role) {
      query = query.eq('role', filters.role)
    }
    if (filters?.schoolId) {
      query = query.eq('school_id', filters.schoolId)
    }
    if (filters?.grade) {
      query = query.eq('grade', filters.grade)
    }
    if (filters?.class) {
      query = filters.classMatch === 'prefix'
        ? query.ilike('class', `${filters.class}%`)
        : query.eq('class', filters.class)
    }
    if (filters?.groupId) {
      query = query.eq('group_members.group_id', filters.groupId)
    }
    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
    }

    const { count, error } = await query
    if (error) throw error
    return count ?? 0
  }

  // Get user by ID
  static async getUser(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        schools(name)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  }

  // Get user by email
  static async getUserByEmail(email: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null
      }
      throw error
    }
    return data
  }

  // Create new user
  static async createUser(user: UserInsert): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .insert(user)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update user
  static async updateUser(id: string, updates: UserUpdate): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update user's last login
  static async updateLastLogin(id: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({
        last_login: new Date().toISOString()
      })
      .eq('id', id)

    if (error) throw error
  }

  // Delete user (admin only)
  static async deleteUser(id: string): Promise<void> {
    // Get current session for auth token
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Authentication required')
    }

    // Call Edge Function to delete user from both Auth and custom table
    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { userId: id },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    })

    if (error) {
      console.error('Edge function error:', error)
      throw new Error(`Failed to delete user: ${error.message}`)
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to delete user')
    }
  }

  // Get user statistics
  static async getUserStats() {
    const { data: users, error } = await supabase
      .from('users')
      .select('role, created_at, last_login')

    if (error) throw error

    const now = new Date()
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    const stats = (users || []).reduce((acc, user) => {
      // Count by role
      acc.byRole[user.role] = (acc.byRole[user.role] || 0) + 1

      // Count new registrations
      const createdAt = new Date(user.created_at)
      if (createdAt > thirtyDaysAgo) {
        acc.newRegistrations.last30Days++
      }
      if (createdAt > sevenDaysAgo) {
        acc.newRegistrations.last7Days++
      }

      // Count active users (logged in last 30 days)
      if (user.last_login) {
        const lastLogin = new Date(user.last_login)
        if (lastLogin > thirtyDaysAgo) {
          acc.activeUsers.last30Days++
        }
        if (lastLogin > sevenDaysAgo) {
          acc.activeUsers.last7Days++
        }
      }

      return acc
    }, {
      byRole: {} as Record<string, number>,
      newRegistrations: {
        last7Days: 0,
        last30Days: 0
      },
      activeUsers: {
        last7Days: 0,
        last30Days: 0
      },
      total: users?.length || 0
    })

    return stats
  }

  // Get user's learning progress
  static async getUserLearningProgress(userId: string) {
    const { data: progress, error: progressError } = await supabase
      .from('user_video_progress')
      .select(`
        *,
        videos(title, duration, category)
      `)
      .eq('user_id', userId)

    if (progressError) throw progressError

    const { data: analytics, error: analyticsError } = await supabase
      .from('video_analytics')
      .select('event_type, timestamp')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false })

    if (analyticsError) throw analyticsError

    const completedVideos = (progress || []).filter(p => p.completed).length
    const totalWatchTime = (progress || []).reduce((sum, p) => sum + p.progress_seconds, 0)
    const lastActivity = analytics?.[0]?.timestamp || null

    return {
      completedVideos,
      totalVideos: progress?.length || 0,
      totalWatchTime,
      lastActivity,
      recentProgress: (progress || [])
        .sort((a, b) => new Date(b.last_watched).getTime() - new Date(a.last_watched).getTime())
        .slice(0, 5)
    }
  }

  // Search users (admin only)
  static async searchUsers(query: string): Promise<User[]> {
    const { data, error} = await supabase
      .from('users')
      .select(`
        *,
        schools(name)
      `)
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .limit(10)

    if (error) throw error
    return data || []
  }

  // Get students with activity counts for a teacher's school
  static async getStudentsWithActivity(
    schoolId: string,
    filters?: {
      grade?: string
      class?: string
      groupId?: string
    }
  ) {
    let query = applyRosterOrder(
      supabase
        .from('users')
        .select('*')
        .eq('school_id', schoolId)
        .eq('role', 'student')
    )

    if (filters?.grade) {
      query = query.eq('grade', filters.grade)
    }

    if (filters?.class) {
      query = query.eq('class', filters.class)
    }

    let students: any[] = []
    let error: any = null

    // If filtering by group, use a different query with join
    // Use the specific foreign key relationship since there are two (student_id and added_by)
    if (filters?.groupId) {
      const { data: groupMembers, error: groupError } = await supabase
        .from('group_members')
        .select(`
          student_id,
          users!group_members_student_id_fkey (*)
        `)
        .eq('group_id', filters.groupId)

      error = groupError
      // Filter by school_id and role on the client side since we can't filter on joined table easily
      students = groupMembers
        ?.map((gm: any) => gm.users)
        .filter((user: any) => user && user.school_id === schoolId && user.role === 'student')
        .sort(compareStudentsForRoster) || []
    } else {
      const { data: studentsData, error: studentsError } = await query
      error = studentsError
      students = studentsData || []
    }

    if (error) {
      console.error('Error fetching students:', error)
      throw error
    }

    if (!students || students.length === 0) {
      return []
    }

    // Get activity counts for all students using RPC function
    const { data: activityCounts, error: activityError } = await supabase
      .rpc('get_student_activity_counts', {
        student_ids: students.map(s => s.id)
      })

    if (activityError) {
      console.error('Error fetching activity counts:', activityError)
    }

    // Create lookup map for O(1) access
    const activityMap = new Map<string, { videoCount: number; chatCount: number }>()
    activityCounts?.forEach((ac: { user_id: string; watched_videos_count: number; active_conversations_count: number }) => {
      activityMap.set(ac.user_id, {
        videoCount: Number(ac.watched_videos_count),
        chatCount: Number(ac.active_conversations_count)
      })
    })

    // Combine data
    return students.map(student => {
      const activity = activityMap.get(student.id)
      return {
        ...student,
        videoCount: activity?.videoCount || 0,
        chatCount: activity?.chatCount || 0
      }
    })
  }

  // 宿題・お知らせの対象学年／クラスの選択肢（学校の生徒の実データ。database/115 の関数でDB側DISTINCT）
  static async getSchoolGradeClassOptions(schoolId: string): Promise<{ grades: string[]; classes: string[] }> {
    const rows = await UserService.getSchoolGradeClassPairs(schoolId)
    const unique = (values: (string | null)[]) =>
      Array.from(new Set(values.filter((v): v is string => !!v))).sort(compareRosterValue)
    return {
      grades: unique(rows.map(r => r.grade)),
      classes: unique(rows.map(r => r.class)),
    }
  }

  // 学校の生徒の（学年, クラス）の組み合わせ。学年ごとのクラスの選択肢を作る絞り込みバー用
  static async getSchoolGradeClassPairs(schoolId: string): Promise<{ grade: string | null; class: string | null }[]> {
    const { data, error } = await supabase.rpc('get_school_grade_class_options', { p_school_id: schoolId })
    if (error) throw error
    return (data ?? []) as { grade: string | null; class: string | null }[]
  }
}
